import React, { useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface DeactivateUserModalProps {
  count: number;
  onConfirm: (dontShowAgain: boolean) => void;
  onCancel: () => void;
  hideDontShowAgain?: boolean;
}

export function DeactivateUserModal({ count, onConfirm, onCancel, hideDontShowAgain }: DeactivateUserModalProps) {
  const { colors } = useTheme();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {/* Header */}
      <View
        style={[styles.header, { backgroundColor: colors.attention.background, borderColor: colors.attention.border }]}
      >
        <MaterialCommunityIcons name="alert-circle" size={24} color={colors.attention.text} />
        <ThemedText type="subtitle" style={{ color: colors.attention.text, marginLeft: 8 }}>
          Deactivate User{count !== 1 ? "s" : ""}
        </ThemedText>
      </View>

      {/* Body */}
      <View style={styles.body}>
        <ThemedText style={styles.message}>
          You&apos;re about to change the status of <ThemedText style={styles.count}>{count}</ThemedText> user
          {count !== 1 ? "s" : ""} to a non-active status.
        </ThemedText>

        <View
          style={[
            styles.warningBox,
            { backgroundColor: colors.attention.background, borderColor: colors.attention.border },
          ]}
        >
          <MaterialCommunityIcons name="account-lock" size={20} color={colors.attention.text} />
          <ThemedText style={[styles.warningText, { color: colors.attention.text }]}>
            This will prevent {count !== 1 ? "these users" : "this user"} from logging in.
          </ThemedText>
        </View>

        {/* Don't show again checkbox */}
        {!hideDontShowAgain && (
          <Pressable style={styles.checkboxRow} onPress={() => setDontShowAgain(!dontShowAgain)}>
            <MaterialCommunityIcons
              name={dontShowAgain ? "checkbox-marked" : "checkbox-blank-outline"}
              size={22}
              color={dontShowAgain ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={[styles.checkboxLabel, { color: colors.textSecondary }]}>
              Don&apos;t show this message again
            </ThemedText>
          </Pressable>
        )}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Confirm"
          icon="check"
          onPress={() => onConfirm(dontShowAgain)}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          iconStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 420,
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  body: {
    padding: 16,
    gap: 16,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
  },
  count: {
    fontWeight: "700",
    fontSize: 16,
  },
  warningBox: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
  },
  warningText: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkboxLabel: {
    fontSize: 13,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    padding: 12,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
